/**
 * StormcallerSkillEffect — Lightning tree fork-2 active: channeled AoE storm.
 *
 * Plant your feet and call down lightning strikes in a targeted area. The player
 * is rooted for the entire channel — can't move, can't cancel. Each strike hits
 * all enemies in the zone. Consumes Static charge at activation, bonus damage
 * split evenly across all strikes.
 *
 * The channel is truly uninterruptible by design: commit to standing still, and
 * the zone eats everything in it. Grounded (upgrade) layers damage reduction
 * and a melee proximity zap onto the same window. Eye of It (passive) debuffs
 * every enemy the storm touches.
 *
 * Upgrade keys consumed via UpgradeEffectResolver (scoped to 'stormcaller'):
 * - `strikeCount` (additive) — +3 from Pressure System
 * - `durationMs` (additive) — +1500ms from Pressure System
 * - `channelingDamageReduction` (additive) — 0.2 from Grounded
 * - `meleeEntryDamage` (additive) — 8 from Grounded
 * - `meleeEntryStaggerMs` (additive) — 300ms from Grounded
 * - `postStrikeOutputReduction` (additive) — 0.15 from Eye of It
 * - `postStrikeDebuffDurationMs` (additive) — 3000ms from Eye of It
 *
 * Fizzle: cooldown consumed, channel never begins.
 * Channel cannot be cancelled once started (deactivate is a no-op during channel).
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md § Stormcaller
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StormcallerDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
    isImmune(effectId: string): boolean;
  };
  /** Consume all charge for the given ID. Returns amount consumed. */
  readonly consumeCharge: (chargeId: string) => number;
  /** Return entity IDs of all enemies within radiusTiles of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];
  /** Return the target zone center position (locked at activation). */
  readonly getTargetPosition: () => { x: number; y: number };
  /** Return the player's current position (for Grounded melee zap). */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Return true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct damage to a target. */
  readonly dealDamage: (entityId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StormcallerConfig {
  /** Post-channel cooldown in ms. Long tier = 2000ms. */
  readonly cooldownMs: number;
  /** Base damage per strike. */
  readonly baseDamagePerStrike: number;
  /** Base number of lightning strikes in the channel. */
  readonly baseStrikeCount: number;
  /** Base channel duration in ms. */
  readonly baseDurationMs: number;
  /** Radius of the target zone in tiles. */
  readonly baseZoneRadiusTiles: number;
  /** Ratio of consumed charge converted to bonus damage. */
  readonly chargeBonusDamageRatio: number;
  /** Charge ID to consume from ChargeSystem. */
  readonly chargeId: string;
  /** Per-enemy cooldown for Grounded melee zap in ms. */
  readonly meleeZapCooldownMs: number;
  /** Range for Grounded melee zap in tiles. */
  readonly meleeZapRangeTiles: number;
}

const DEFAULT_CONFIG: StormcallerConfig = {
  cooldownMs: 2000,              // long cooldown tier
  baseDamagePerStrike: 6,        // moderate per-strike for AoE
  baseStrikeCount: 6,            // 6 strikes over 3 seconds
  baseDurationMs: 3000,          // 3-second channel
  baseZoneRadiusTiles: 2.5,      // spec recommendation
  chargeBonusDamageRatio: 0.1,   // matches Static passive default
  chargeId: 'static_charge',
  meleeZapCooldownMs: 500,       // Grounded: one zap per enemy per 500ms
  meleeZapRangeTiles: 1.5,       // Grounded: melee proximity threshold
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StormcallerSkillEffect implements SkillEffect {
  readonly skillId = 'stormcaller';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly _deps: StormcallerDeps;
  private readonly _config: StormcallerConfig;

  // Cooldown
  private _cooldownRemaining = 0;

  // Channel state
  private _isChanneling = false;
  private _strikeTimer = 0;
  private _strikesDelivered = 0;
  private _totalStrikes = 0;
  private _strikeInterval = 0;
  private _targetPos: { x: number; y: number } | null = null;
  private _chargeBonusPerStrike = 0;

  // Grounded melee zap cooldowns (per enemy ID)
  private readonly _meleeZapCooldowns = new Map<string, number>();

  // Observable counters
  private _activations = 0;
  private _fizzles = 0;
  private _totalStrikesFired = 0;
  private _totalDamageEvents = 0;
  private _totalMeleeZaps = 0;
  private _debuffsApplied = 0;
  private _channelsCompleted = 0;

  // Timing and resolved values
  private _elapsedMs = 0;
  private readonly _strikeTimesMs: number[] = [];
  private _resolvedDurationMs = 0;

  constructor(deps: StormcallerDeps, config: Partial<StormcallerConfig> = {}) {
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get totalStrikesFired(): number { return this._totalStrikesFired; }
  get totalDamageEvents(): number { return this._totalDamageEvents; }
  get totalMeleeZaps(): number { return this._totalMeleeZaps; }
  get debuffsApplied(): number { return this._debuffsApplied; }
  get strikesDelivered(): number { return this._strikesDelivered; }
  get resolvedStrikeCount(): number { return this._totalStrikes; }
  get resolvedStrikeInterval(): number { return this._strikeInterval; }
  get isChanneling(): boolean { return this._isChanneling; }
  get strikesFired(): number { return this._totalStrikesFired; }
  get meleeZapCount(): number { return this._totalMeleeZaps; }
  get channelsCompleted(): number { return this._channelsCompleted; }
  get resolvedChannelDurationMs(): number { return this._resolvedDurationMs; }
  get strikeTimesMs(): readonly number[] { return this._strikeTimesMs; }
  get resolvedPostStrikeReduction(): number {
    return this._deps.resolver.getAdditiveEffect('stormcaller', 'postStrikeOutputReduction');
  }
  get resolvedPostStrikeDurationMs(): number {
    return this._deps.resolver.getAdditiveEffect('stormcaller', 'postStrikeDebuffDurationMs');
  }

  /**
   * Compute incoming damage after Grounded's damage reduction.
   * Returns full damage when not channeling or Grounded not unlocked.
   */
  applyIncomingDamage(amount: number): number {
    return amount * (1 - this.channelingDamageReduction);
  }

  /**
   * Channeling damage reduction from Grounded upgrade.
   * The game layer queries this during the channel to reduce incoming damage.
   * Returns 0 at base (no Grounded = no reduction).
   */
  get channelingDamageReduction(): number {
    if (!this._isChanneling) return 0;
    return this._deps.resolver.getAdditiveEffect('stormcaller', 'channelingDamageReduction');
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._isChanneling;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed, channel never begins
    if (!this._deps.rollSkillSuccess('stormcaller')) {
      this._fizzles++;
      this._cooldownRemaining = this._config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this._deps.breakHide();

    // Lock target position at activation
    this._targetPos = this._deps.getTargetPosition();

    // Consume Static charge — bonus split evenly across all strikes
    const chargeConsumed = this._deps.consumeCharge(this._config.chargeId);
    const totalChargeBonus = chargeConsumed * this._config.chargeBonusDamageRatio;

    // Resolve strike count and duration from upgrades
    const resolvedStrikes = this._config.baseStrikeCount
      + this._deps.resolver.getAdditiveEffect('stormcaller', 'strikeCount');
    const resolvedDuration = this._config.baseDurationMs
      + this._deps.resolver.getAdditiveEffect('stormcaller', 'durationMs');

    this._totalStrikes = resolvedStrikes;
    this._resolvedDurationMs = resolvedDuration;
    this._strikeInterval = resolvedDuration / resolvedStrikes;
    this._chargeBonusPerStrike = resolvedStrikes > 0 ? totalChargeBonus / resolvedStrikes : 0;

    // Enter channel state
    this._isChanneling = true;
    this._strikesDelivered = 0;
    this._strikeTimer = this._strikeInterval; // first strike after one interval
    this._meleeZapCooldowns.clear();

    this._activations++;
    return true;
  }

  /**
   * Channel cannot be cancelled once started. The player committed to standing
   * still — the storm finishes on its own terms.
   * See spec: "Cannot be cancelled mid-channel."
   */
  deactivate(): void {
    // Intentionally empty — Stormcaller channel is uninterruptible.
  }

  isActive(): boolean { return this._isChanneling; }

  update(delta: number): void {
    this._elapsedMs += delta;

    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._isChanneling) return;

    // Tick strike timer
    this._strikeTimer -= delta;

    // Fire strikes when timer expires
    while (this._strikeTimer <= 0 && this._strikesDelivered < this._totalStrikes) {
      this._fireStrike();
      this._strikesDelivered++;
      this._totalStrikesFired++;

      if (this._strikesDelivered < this._totalStrikes) {
        this._strikeTimer += this._strikeInterval;
      }
    }

    // Grounded melee proximity zap (only fires when meleeEntryDamage > 0)
    this._tickMeleeZap(delta);

    // Channel complete when all strikes delivered
    if (this._strikesDelivered >= this._totalStrikes) {
      this._endChannel();
    }
  }

  destroy(): void {
    this._isChanneling = false;
    this._cooldownRemaining = 0;
    this._strikeTimer = 0;
    this._targetPos = null;
    this._meleeZapCooldowns.clear();
  }

  // ---------------------------------------------------------------------------
  // Strike logic — each strike hits all enemies in the target zone
  // ---------------------------------------------------------------------------

  private _fireStrike(): void {
    if (!this._targetPos) return;
    this._strikeTimesMs.push(this._elapsedMs);

    const enemies = this._deps.getEnemiesInRadius(
      this._targetPos.x,
      this._targetPos.y,
      this._config.baseZoneRadiusTiles,
    );

    const damagePerHit = this._config.baseDamagePerStrike + this._chargeBonusPerStrike;

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, damagePerHit);
      this._totalDamageEvents++;
    }

    // Eye of It pre-wire: debuff struck enemies if passive is active
    const debuffIntensity = this._deps.resolver.getAdditiveEffect(
      'stormcaller', 'postStrikeOutputReduction',
    );
    const debuffDuration = this._deps.resolver.getAdditiveEffect(
      'stormcaller', 'postStrikeDebuffDurationMs',
    );

    if (debuffIntensity > 0 && debuffDuration > 0) {
      for (const enemyId of enemies) {
        // STUB: deferred — no enemy awareness/detection system reads this debuff
        this._deps.statusEffects.apply('eye_of_it_debuff', {
          sourceSkillId: 'stormcaller',
          durationMs: debuffDuration,
          intensity: debuffIntensity,
          stacks: 1,
        });
        this._debuffsApplied++;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Grounded melee zap — punishes enemies that rush the caster
  // ---------------------------------------------------------------------------

  private _tickMeleeZap(delta: number): void {
    const meleeEntryDamage = this._deps.resolver.getAdditiveEffect(
      'stormcaller', 'meleeEntryDamage',
    );
    if (meleeEntryDamage <= 0) return;

    // Decrement per-enemy cooldowns
    for (const [enemyId, remaining] of this._meleeZapCooldowns) {
      const newRemaining = remaining - delta;
      if (newRemaining <= 0) {
        this._meleeZapCooldowns.delete(enemyId);
      } else {
        this._meleeZapCooldowns.set(enemyId, newRemaining);
      }
    }

    // Check enemies in melee range of the rooted player
    const playerPos = this._deps.getPlayerPosition();
    const meleeEnemies = this._deps.getEnemiesInRadius(
      playerPos.x,
      playerPos.y,
      this._config.meleeZapRangeTiles,
    );

    const staggerMs = this._deps.resolver.getAdditiveEffect(
      'stormcaller', 'meleeEntryStaggerMs',
    );

    for (const enemyId of meleeEnemies) {
      if (this._meleeZapCooldowns.has(enemyId)) continue;

      // Deal zap damage
      this._deps.dealDamage(enemyId, meleeEntryDamage);
      this._totalMeleeZaps++;

      // Apply stagger if not immune
      if (staggerMs > 0 && !this._deps.statusEffects.isImmune('stagger')) {
        this._deps.statusEffects.apply('stagger', {
          sourceSkillId: 'stormcaller',
          durationMs: staggerMs,
          intensity: 1,
          stacks: 1,
        });
      }

      // Set per-enemy zap cooldown
      this._meleeZapCooldowns.set(enemyId, this._config.meleeZapCooldownMs);
    }
  }

  // ---------------------------------------------------------------------------
  // Channel completion — the storm passes
  // ---------------------------------------------------------------------------

  private _endChannel(): void {
    this._isChanneling = false;
    this._strikeTimer = 0;
    this._targetPos = null;
    this._meleeZapCooldowns.clear();
    this._cooldownRemaining = this._config.cooldownMs;
    this._channelsCompleted++;
  }
}
